import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ExpenseCategoryForm from '../components/ExpenseCategoryForm';
import PageSuggestion from '../components/PageSuggestion';
import { expenseCategoryApi } from '../services/api';
import {
    ExpenseCategory,
    CreateExpenseCategoryRequest,
    UpdateExpenseCategoryRequest,
} from '../types';

const ExpenseCategories: React.FC = () => {
    const [items, setItems] = useState<ExpenseCategory[]>([]);
    const [filtered, setFiltered] = useState<ExpenseCategory[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<ExpenseCategory | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ExpenseCategory | null>(null);
    const [deleting, setDeleting] = useState(false);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        const f = items.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setFiltered(f);
        setCurrentPage(1);
    }, [searchTerm, items]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const res = await expenseCategoryApi.getAll();
            setItems(res.data || []);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Não foi possível carregar as categorias.';
            setLoadError(msg);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data: CreateExpenseCategoryRequest) => {
        try {
            await expenseCategoryApi.create(data);
            await fetchItems();
            setIsModalOpen(false);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
            setLoadError(typeof msg === 'string' ? msg : 'Falha ao criar categoria.');
        }
    };

    const handleUpdate = async (data: UpdateExpenseCategoryRequest) => {
        if (!editing) return;
        try {
            await expenseCategoryApi.update(editing.id, data);
            await fetchItems();
            setIsModalOpen(false);
            setEditing(null);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Falha ao atualizar categoria.';
            setLoadError(msg);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await expenseCategoryApi.delete(deleteTarget.id);
            setDeleteTarget(null);
            await fetchItems();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Falha ao excluir categoria.';
            setLoadError(msg);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        { key: 'name', label: 'Nome' },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => new Date(value).toLocaleDateString('pt-BR'),
        },
    ];

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {loadError && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{loadError}</div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Categorias de gastos</h1>
                    <p className="text-secondary-600">Cadastre categorias para organizar seus gastos</p>
                    <PageSuggestion>
                        Defina categorias claras para conseguir filtrar despesas, comparar meses e gerar relatorios mais uteis.
                    </PageSuggestion>
                </div>
                <button onClick={() => { setEditing(null); setIsModalOpen(true); }} className="btn-primary flex items-center justify-center">
                    <Plus className="h-4 w-4 mr-2" />
                    Nova categoria
                </button>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por nome..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={(row) => { setEditing(row); setIsModalOpen(true); }}
                editButtonText="Editar"
                onDelete={(row) => setDeleteTarget(row)}
                deleteButtonText="Excluir"
            />

            <Modal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                title={editing ? 'Editar categoria' : 'Nova categoria'}
            >
                <ExpenseCategoryForm
                    category={editing}
                    onSubmit={editing ? handleUpdate : handleCreate}
                    onCancel={() => { setIsModalOpen(false); setEditing(null); }}
                />
            </Modal>

            <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Excluir categoria">
                <p className="text-secondary-700 mb-4">
                    Tem certeza que deseja excluir a categoria &quot;{deleteTarget?.name}&quot;?
                </p>
                <div className="flex justify-end gap-3">
                    <button type="button" className="btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancelar
                    </button>
                    <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Excluindo...' : 'Excluir'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default ExpenseCategories;
